import React from 'react'
import { ProviderType } from '../generated/graphql'
import { ProviderConstructor } from '../calculators/providers/common/Provider'
import { ElectricalGridProvider } from '../calculators/providers/ElectricalGridProvider'
import { DistrictCoolingProvider } from '../calculators/providers/DistrictCoolingProvider'
import { HeatingAirHeatPumpProvider } from '../calculators/providers/HeatingAirHeatPumpProvider'
import { ElectricBoilerProvider } from '../calculators/providers/ElectricBoilerProvider'
import { WaterCoolingHeatPumpProvider } from '../calculators/providers/WaterCoolingHeatPumpProvider'
import { SolarPanelProvider } from '../calculators/providers/SolarPanelProvider'
import { EnergyType } from '../calculators/EnergyFlow'
import { InefficientDeviceForm } from '../components/system-container/provider-dialog/parameters/InefficientDeviceForm'
import { HeatingAirHeatPumpForm } from '../components/system-container/provider-dialog/parameters/HeatingAirHeatPumpForm'
import { WaterCoolingHeatPumpForm } from '../components/system-container/provider-dialog/parameters/WaterCoolingHeatPumpForm'
import { DistrictHeatingProvider } from '../calculators/providers/DistrictHeatingProvider'
import { CoolingAirHeatPumpForm } from '../components/system-container/provider-dialog/parameters/CoolingAirHeatPumpForm'
import { CoolingAirHeatPumpProvider } from '../calculators/providers/CoolingAirHeatPumpProvider'
import { CoolingGroundHeatPumpForm } from '../components/system-container/provider-dialog/parameters/CoolingGroundHeatPumpForm'
import { CoolingGroundHeatPumpProvider } from '../calculators/providers/CoolingGroundHeatPumpProvider'
import { CirculationHeatPumpForm } from '../components/system-container/provider-dialog/parameters/CirculationHeatPumpForm'
import { CirculationHeatPumpProvider } from '../calculators/providers/CirculationHeatPumpProvider'
import { LocalWaterGridProvider } from '../calculators/providers/LocalWaterGridProvider'
import { LocalElectricalGridProvider } from '../calculators/providers/LocalElectricalGridProvider'
import { SolarPanelForm } from '../components/system-container/provider-dialog/parameters/SolarPanelForm'
import { HeatingGroundHeatPumpProvider } from '../calculators/providers/HeatingGroundHeatPumpProvider'
import { HeatingGroundHeatPumpForm } from '../components/system-container/provider-dialog/parameters/HeatingGroundHeatPumpForm'
import { PreheaterForm } from '../components/system-container/provider-dialog/parameters/PreheaterForm'
import { PreheaterProvider } from '../calculators/providers/PreheaterProvider'
import { HeatBoilerProvider } from '../calculators/providers/HeatBoilerProvider'
import { HeatBoilerForm } from '../components/system-container/provider-dialog/parameters/HeatBoilerForm'
import { HybridHeatPumpForm } from '../components/system-container/provider-dialog/parameters/HybridHeatPumpForm'
import { HybridHeatPumpProvider } from '../calculators/providers/HybridHeatPumpProvider'
import { BatteryProvider } from '../calculators/providers/BatteryProvider'
import { BatteryForm } from '../components/system-container/provider-dialog/parameters/BatteryForm'

export enum ParameterType {
  Empty = 'Empty',
  Efficiency = 'Efficiency',
  MaxPower = 'MaxPower',
  HeatingPower = 'HeatingPower',
  PreheaterPowerCoverage = 'PreheaterPowerCoverage',
  Multiplier = 'Multiplier',
  Cop = 'Cop',
  Eer = 'Eer',
  EerFromCop = 'EerFromCop',
  Spf = 'Spf',
  Area = 'Area',
  SolarEfficiency = 'SolarEfficiency',
  Overproduction = 'Overproduction',
  EnergyCapacity = 'EnergyCapacity',
}

type ProviderProperties = {
  providerClass?: ProviderConstructor
  providesEnergyTypes: EnergyType[]
  priority: number
  form?: React.ElementType
  primaryParameterType: ParameterType
  getSecondaryParameterType?: (energyType: EnergyType) => ParameterType
  reportParameterType: ParameterType
  isStatic?: boolean
  isUnrestricted?: boolean
  isPurchasedEnergy?: boolean
  isFuelUser?: boolean
  isLocalEnergy?: boolean
  isGreenEnergy?: boolean
  isSolarEnergy?: boolean
  isEnvironmentalHeat?: boolean
  isWasteEnergyBased?: boolean
  isElectricityPurchasing?: boolean
  isGroundHeatUsing?: boolean
  isGroundHeatGiving?: boolean
  canSellWaste?: boolean
  canUseEnergyBus?: boolean
}

export type ProviderProperty = keyof ProviderProperties

export const providerProperties: Record<ProviderType, ProviderProperties> = {
  // Purchased energy sources.

  [ProviderType.DistrictHeating]: {
    providerClass: DistrictHeatingProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 1000,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isStatic: true,
    isUnrestricted: true,
    isPurchasedEnergy: true,
  },

  [ProviderType.DistrictCooling]: {
    providerClass: DistrictCoolingProvider,
    providesEnergyTypes: [EnergyType.Cooling],
    priority: 1001,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isStatic: true,
    isUnrestricted: true,
    isPurchasedEnergy: true,
  },

  [ProviderType.ElectricalGrid]: {
    providerClass: ElectricalGridProvider,
    providesEnergyTypes: [EnergyType.Electricity],
    priority: 2202,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isStatic: true,
    isUnrestricted: true,
    isPurchasedEnergy: true,
  },

  // Local energy sources.

  [ProviderType.LocalWaterGrid]: {
    providerClass: LocalWaterGridProvider,
    providesEnergyTypes: [EnergyType.Heating, EnergyType.Cooling],
    priority: 100,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isLocalEnergy: true,
  },

  [ProviderType.LocalElectricalGrid]: {
    providerClass: LocalElectricalGridProvider,
    providesEnergyTypes: [EnergyType.Electricity],
    priority: 2102,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isLocalEnergy: true,
  },

  // Self-sufficient energy sources.

  [ProviderType.HeatingGroundHeatPump]: {
    providerClass: HeatingGroundHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 50,
    form: HeatingGroundHeatPumpForm,
    primaryParameterType: ParameterType.MaxPower,
    getSecondaryParameterType: () => ParameterType.Cop,
    reportParameterType: ParameterType.Cop,
    isEnvironmentalHeat: true,
    isElectricityPurchasing: true,
    isGroundHeatUsing: true,
  },

  [ProviderType.HeatingAirHeatPump]: {
    providerClass: HeatingAirHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 60,
    form: HeatingAirHeatPumpForm,
    primaryParameterType: ParameterType.MaxPower,
    getSecondaryParameterType: () => ParameterType.Cop,
    reportParameterType: ParameterType.Cop,
    isEnvironmentalHeat: true,
    isElectricityPurchasing: true,
  },

  [ProviderType.HeatBoiler]: {
    providerClass: HeatBoilerProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 109,
    form: HeatBoilerForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isUnrestricted: true,
    isFuelUser: true,
  },

  [ProviderType.ElectricBoiler]: {
    providerClass: ElectricBoilerProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 110,
    form: InefficientDeviceForm,
    primaryParameterType: ParameterType.Efficiency,
    reportParameterType: ParameterType.Efficiency,
    isUnrestricted: true,
    isElectricityPurchasing: true,
  },

  [ProviderType.Preheater]: {
    providerClass: PreheaterProvider,
    providesEnergyTypes: [EnergyType.Heating],
    priority: 10,
    form: PreheaterForm,
    primaryParameterType: ParameterType.PreheaterPowerCoverage,
    reportParameterType: ParameterType.Empty,
    isWasteEnergyBased: true,
  },

  [ProviderType.CoolingGroundHeatPump]: {
    providerClass: CoolingGroundHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Cooling],
    priority: 51,
    form: CoolingGroundHeatPumpForm,
    primaryParameterType: ParameterType.MaxPower,
    getSecondaryParameterType: () => ParameterType.Eer,
    reportParameterType: ParameterType.Eer,
    isElectricityPurchasing: true,
    isGroundHeatGiving: true,
  },

  [ProviderType.CoolingAirHeatPump]: {
    providerClass: CoolingAirHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Cooling],
    priority: 61,
    form: CoolingAirHeatPumpForm,
    primaryParameterType: ParameterType.MaxPower,
    getSecondaryParameterType: () => ParameterType.Eer,
    reportParameterType: ParameterType.Eer,
    isElectricityPurchasing: true,
  },

  [ProviderType.WaterCoolingHeatPump]: {
    providerClass: WaterCoolingHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Cooling],
    priority: 201,
    form: WaterCoolingHeatPumpForm,
    primaryParameterType: ParameterType.Eer,
    reportParameterType: ParameterType.Eer,
    isUnrestricted: true,
    isElectricityPurchasing: true,
  },

  [ProviderType.CirculationHeatPump]: {
    providerClass: CirculationHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Heating, EnergyType.Cooling],
    priority: 30,
    form: CirculationHeatPumpForm,
    primaryParameterType: ParameterType.MaxPower,
    getSecondaryParameterType: (energyType: EnergyType) =>
      energyType === EnergyType.Heating
        ? ParameterType.Cop
        : ParameterType.EerFromCop,
    reportParameterType: ParameterType.MaxPower,
    // Circulation heat pump is waste energy based only when connected to waste heat
    // Also see: calculator-result-utility > createFilterProviderByPropertyIfExists
    isWasteEnergyBased: true,
    isElectricityPurchasing: true,
  },

  [ProviderType.HybridHeatPump]: {
    providerClass: HybridHeatPumpProvider,
    providesEnergyTypes: [EnergyType.Heating, EnergyType.Cooling],
    priority: 40,
    form: HybridHeatPumpForm,
    primaryParameterType: ParameterType.HeatingPower,
    getSecondaryParameterType: () => ParameterType.Spf,
    reportParameterType: ParameterType.Spf,
    isEnvironmentalHeat: true,
    isWasteEnergyBased: true,
    isElectricityPurchasing: true,
    canUseEnergyBus: true,
  },

  [ProviderType.SolarPanel]: {
    providerClass: SolarPanelProvider,
    providesEnergyTypes: [EnergyType.Electricity],
    priority: 2002,
    form: SolarPanelForm,
    primaryParameterType: ParameterType.Area,
    getSecondaryParameterType: () => ParameterType.Overproduction,
    reportParameterType: ParameterType.SolarEfficiency,
    isGreenEnergy: true,
    isSolarEnergy: true,
    canSellWaste: true,
  },

  [ProviderType.Battery]: {
    providerClass: BatteryProvider,
    providesEnergyTypes: [EnergyType.Electricity],
    priority: 2052,
    form: BatteryForm,
    primaryParameterType: ParameterType.EnergyCapacity,
    reportParameterType: ParameterType.EnergyCapacity,
    isGreenEnergy: true,
  },
}

const filterTypesByProperties = (
  predicate: (properties: ProviderProperties) => boolean | undefined,
) =>
  Object.values(ProviderType).filter((providerType) =>
    predicate(providerProperties[providerType]),
  )

export const staticProviderTypes = filterTypesByProperties(
  (properties) => properties.isStatic,
)
export const localEnergyProviderTypes = filterTypesByProperties(
  (properties) => properties.isLocalEnergy,
)
export const groundHeatUsingProviderTypes = filterTypesByProperties(
  (properties) => properties.isGroundHeatUsing,
)
export const groundHeatGivingProviderTypes = filterTypesByProperties(
  (properties) => properties.isGroundHeatGiving,
)
export const electricityPurchasingHeatingProviderTypes =
  filterTypesByProperties(
    (properties) =>
      properties.isElectricityPurchasing &&
      properties.providesEnergyTypes?.includes(EnergyType.Heating),
  )
export const electricityPurchasingCoolingProviderTypes =
  filterTypesByProperties(
    (properties) =>
      properties.isElectricityPurchasing &&
      properties.providesEnergyTypes?.includes(EnergyType.Cooling),
  )
export const electricityPurchasingProviderTypes = filterTypesByProperties(
  (properties) => properties.isElectricityPurchasing,
)
export const electricityProviderTypes = filterTypesByProperties((properties) =>
  properties.providesEnergyTypes.includes(EnergyType.Electricity),
)

export const energyTypeToProviderTypesMap = new Map<EnergyType, ProviderType[]>(
  Object.values(EnergyType).map((energyType) => [
    energyType,
    filterTypesByProperties((properties) =>
      properties.providesEnergyTypes.includes(energyType),
    ),
  ]),
)

export function getProviderTypesByEnergyType(
  energyType: EnergyType,
  localElectricityGridExists?: boolean,
  localWaterGridExists?: boolean,
): ProviderType[] {
  const providerTypes = energyTypeToProviderTypesMap.get(energyType)

  if (!providerTypes) {
    throw new Error(
      `Unable to map energy type '${energyType}' to provider types`,
    )
  }

  return providerTypes
    .filter(
      (type) =>
        localElectricityGridExists || type !== ProviderType.LocalElectricalGrid,
    )
    .filter(
      (type) => localWaterGridExists || type !== ProviderType.LocalWaterGrid,
    )
}
